import { useCallback, useMemo, useRef } from "react";
import {
  type NotificationRow,
  NotificationsViewDataDocument,
  type NotificationsViewDataQuery,
} from "#app/lib/data";
import { usePagedConnection } from "#app/lib/hooks/use-paged-connection";

interface UseInboxOptions {
  showCleared: boolean;
}

const PAGE_SIZE = 50;

export function useInbox({ showCleared }: UseInboxOptions) {
  const variables = useMemo(() => ({ showCleared }), [showCleared]);

  // Capture unreadCount from the watched payload — only honor it on the
  // non-cleared tab so the badge always reflects the "All" count.
  const stableUnreadCount = useRef(0);
  const onData = useCallback(
    (data: NotificationsViewDataQuery | undefined) => {
      const next = data?.notifications?.unreadCount ?? 0;
      if (!showCleared) stableUnreadCount.current = next;
    },
    [showCleared],
  );

  const {
    items,
    total,
    hasMore,
    loading,
    loadingMore,
    fetchPage,
    refetch,
  } = usePagedConnection<NotificationRow>({
    document: NotificationsViewDataDocument,
    variables,
    pageSize: PAGE_SIZE,
    getConnection: (d) => d?.notifications,
    onData,
  });

  return {
    items,
    total,
    loading,
    loadingMore,
    hasMore,
    fetchPage,
    error: undefined,
    refetch,
    unreadCount: stableUnreadCount.current,
  };
}
